import React, { useEffect, useRef, useState } from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { IconBrandMeta } from '@tabler/icons-react';
import axios from '../../config/axios';
import { config, inferEmbeddedSignupTenantFromHost } from '../../config/environment';
import {
  buildEmbeddedSignupFinishRelayUrl,
  isMetaEmbeddedSignupMessageOrigin,
  parseWaEmbeddedSignupFinishPayload,
} from '../../config/embeddedSignup';

export interface ConnectWhatsAppEmbeddedButtonProps extends Omit<ButtonProps, 'onClick' | 'onError'> {
  /** Caminho no front do tenant após sucesso (ex. /whatsapp-cloud-api). */
  returnPath?: string;
  /** Se não vier de env, envie no body (multi-build). */
  tenantSubdomain?: string;
  /** Erro ao chamar /prepare (não usar `onError` — conflita com o handler nativo do botão). */
  onPrepareError?: (err: Error) => void;
  /** Sucesso apenas nos fluxos legados (postMessage). No fluxo OAuth, sucesso/erro são tratados em /facebook/callback. */
  onFinishSuccess?: (payload: { displayPhoneNumber?: string; wabaId?: string }) => void;
  /** Erro apenas nos fluxos legados (postMessage). No fluxo OAuth, sucesso/erro são tratados em /facebook/callback. */
  onFinishError?: (err: Error) => void;
  /** Apenas para o fluxo legado (popup postMessage). Ignorado no fluxo OAuth (default). */
  openInNewTab?: boolean;
}

interface PrepareResponse {
  success?: boolean;
  url?: string;
  state?: string;
  expiresAt?: string;
  tenantSubdomain?: string;
  error?: string;
}

const POPUP_NAME = 'pooh_meta_wa_embedded';
const MESSAGE_WAIT_MS = 15 * 60 * 1000;

type FlowMode = 'oauth' | 'launcher' | 'popup';

/**
 * Embedded Signup Meta — três fluxos suportados:
 *
 *  1) OAuth tradicional (default):
 *     Backend devolve URL `www.facebook.com/{ver}/dialog/oauth?...&redirect_uri=...`.
 *     A janela atual é redirecionada para a Meta; ao concluir, a Meta volta para
 *     `redirect_uri` (ex. `https://app.pooh.pro/facebook/callback?code=&state=`),
 *     e a página de callback chama `/embedded-signup/complete` no backend do tenant
 *     para gravar a config. Não depende de postMessage, popup, COOP, SW ou
 *     Allowed Domains.
 *
 *  2) Launcher canônico (legado postMessage):
 *     Quando `REACT_APP_EMBEDDED_SIGNUP_LAUNCHER_URL` está definido, redireciona-se
 *     a janela atual para o launcher (ex. `https://app.pooh.pro/embedded-signup-launcher`).
 *     O launcher abre o popup da Meta, recebe o postMessage e despacha para
 *     `/finish-relay` no API do tenant.
 *
 *  3) Popup local (legado postMessage):
 *     Quando o backend retorna URL `business.facebook.com/.../onboard/` e o launcher
 *     não está configurado, abrimos o popup no próprio domínio. Só funciona se o
 *     domínio atual estiver em Allowed Domains da app Meta.
 *
 * O fluxo é detectado automaticamente a partir da URL devolvida por /prepare:
 *   - `www.facebook.com` ou contém `dialog/oauth` → OAuth (redireciona a janela).
 *   - `business.facebook.com/.../onboard/` → Hosted (launcher ou popup local).
 */
export const ConnectWhatsAppEmbeddedButton: React.FC<ConnectWhatsAppEmbeddedButtonProps> = ({
  returnPath = '/whatsapp-cloud-api',
  tenantSubdomain,
  children,
  onPrepareError,
  onFinishSuccess,
  onFinishError,
  openInNewTab = false,
  ...btnProps
}) => {
  const [loading, setLoading] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, []);

  const handleClick = async (): Promise<void> => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    setLoading(true);

    try {
      const slug =
        (tenantSubdomain || '').trim() ||
        config.embeddedSignupTenantSubdomain.trim() ||
        inferEmbeddedSignupTenantFromHost() ||
        undefined;

      const { data } = await axios.post<PrepareResponse>(
        '/api/v1/whatsapp-cloud-api/embedded-signup/prepare',
        {
          returnPath,
          tenantSubdomain: slug,
        }
      );

      if (!data?.url) {
        throw new Error(data?.error || 'Resposta inválida do servidor (sem URL Meta).');
      }

      const targetUrl = data.url;
      const flow = detectFlowMode(targetUrl);

      if (flow === 'oauth') {
        console.info('[ConnectWhatsAppEmbedded] OAuth redirect →', targetUrl);
        window.location.href = targetUrl;
        return;
      }

      const state = data.state || extractStateFromUrl(targetUrl);
      const effectiveSlug = (data.tenantSubdomain || slug || '').toLowerCase();
      const launcherConfigured = Boolean(config.embeddedSignupLauncherUrl.trim());

      if (launcherConfigured) {
        if (!state) {
          throw new Error('Backend não devolveu state — atualize o backend para a versão com state explícito.');
        }
        const finishRelayUrl = buildEmbeddedSignupFinishRelayUrl(effectiveSlug);
        if (!finishRelayUrl) {
          throw new Error(
            'REACT_APP_EMBEDDED_SIGNUP_COMPLETE_URL_TEMPLATE ausente no build deste painel — necessário para derivar a URL de finish-relay.'
          );
        }
        const launcherBase = config.embeddedSignupLauncherUrl.trim().replace(/[?#].*$/, '');
        const returnAbs = new URL(returnPath, window.location.origin).toString();

        const launcherUrl = new URL(launcherBase);
        launcherUrl.searchParams.set('metaUrl', targetUrl);
        launcherUrl.searchParams.set('apiFinishUrl', finishRelayUrl);
        launcherUrl.searchParams.set('state', state);
        launcherUrl.searchParams.set('returnUrl', returnAbs);
        launcherUrl.searchParams.set('tenant', effectiveSlug);

        console.info('[ConnectWhatsAppEmbedded] redirecting to launcher:', launcherUrl.toString());
        window.location.href = launcherUrl.toString();
        return;
      }

      await openLegacyPopupFlow({
        targetUrl,
        openInNewTab,
        returnPath,
        cleanupRef,
        setLoading,
        onFinishSuccess,
        onFinishError,
      });
    } catch (e: unknown) {
      setLoading(false);
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      const msg = ax.response?.data?.error || ax.message || (e instanceof Error ? e.message : String(e));
      onPrepareError?.(new Error(msg));
    }
  };

  return (
    <Button
      {...btnProps}
      type="button"
      startIcon={
        loading ? (
          <CircularProgress size={18} color="inherit" />
        ) : (
          <IconBrandMeta size={20} color="#0866FF" aria-hidden />
        )
      }
      disabled={loading || btnProps.disabled}
      onClick={() => void handleClick()}
    >
      {children || (loading ? 'Abrindo Meta...' : 'Conectar diretamente com a Meta')}
    </Button>
  );
};

function detectFlowMode(url: string): FlowMode {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    if (host === 'www.facebook.com' || host === 'facebook.com' || u.pathname.includes('/dialog/oauth')) {
      return 'oauth';
    }
    return 'launcher';
  } catch {
    return 'launcher';
  }
}

function extractStateFromUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.searchParams.get('state') || '';
  } catch {
    return '';
  }
}

async function openLegacyPopupFlow(args: {
  targetUrl: string;
  openInNewTab: boolean;
  returnPath: string;
  cleanupRef: React.MutableRefObject<(() => void) | null>;
  setLoading: (b: boolean) => void;
  onFinishSuccess?: (payload: { displayPhoneNumber?: string; wabaId?: string }) => void;
  onFinishError?: (err: Error) => void;
}): Promise<void> {
  const { targetUrl, openInNewTab, returnPath, cleanupRef, setLoading, onFinishSuccess, onFinishError } = args;
  const features = openInNewTab
    ? 'popup=yes,width=720,height=900,left=60,top=40,menubar=no,toolbar=no,status=no'
    : 'popup=yes,width=600,height=720,left=80,top=80,menubar=no,toolbar=no,status=no';

  const popup = window.open(targetUrl, POPUP_NAME, features);
  if (!popup) {
    setLoading(false);
    throw new Error('Popup bloqueado. Permita pop-ups para este site para concluir o Embedded Signup na Meta.');
  }

  console.info(
    '[ConnectWhatsAppEmbedded] Aguardando postMessage da Meta. Domínio desta página (deve estar em "Allowed Domains" na app Meta):',
    window.location.origin
  );

  const ttl = window.setTimeout(() => {
    window.removeEventListener('message', onMessage);
    cleanupRef.current = null;
    setLoading(false);
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    onFinishError?.(
      new Error(
        'Tempo esgotado aguardando a Meta. O domínio desta página (https://' +
          host +
          ') provavelmente não está em "Allowed Domains" / "Valid OAuth Redirect URIs" da app Meta. ' +
          'Sugestão: ative META_ES_AUTH_MODE=oauth no backend (fluxo redirect tradicional).'
      )
    );
  }, MESSAGE_WAIT_MS);

  const onMessage = (ev: MessageEvent): void => {
    if (!isMetaEmbeddedSignupMessageOrigin(ev.origin)) return;
    const raw = ev.data;
    const parsed = parseWaEmbeddedSignupFinishPayload(raw);
    if (!parsed) {
      if (raw && typeof raw === 'object' && (raw as Record<string, unknown>).type === 'WA_EMBEDDED_SIGNUP') {
        console.warn('[ConnectWhatsAppEmbedded] WA_EMBEDDED_SIGNUP não interpretado:', raw);
      }
      return;
    }
    window.clearTimeout(ttl);
    window.removeEventListener('message', onMessage);
    cleanupRef.current = null;
    void (async () => {
      try {
        console.info('[ConnectWhatsAppEmbedded] WA_EMBEDDED_SIGNUP FINISH:', parsed);
        const fin = await axios.post('/api/v1/whatsapp-cloud-api/embedded-signup/finish', {
          waba_id: parsed.waba_id,
          phone_number_id: parsed.phone_number_id,
          business_id: parsed.business_id,
          returnPath,
        });
        try {
          popup.close();
        } catch {
          /* ignore */
        }
        setLoading(false);
        onFinishSuccess?.({
          displayPhoneNumber: fin.data?.displayPhoneNumber,
          wabaId: fin.data?.wabaId,
        });
      } catch (e: unknown) {
        setLoading(false);
        const ax = e as { response?: { data?: { error?: string } }; message?: string };
        const msg = ax.response?.data?.error || ax.message || (e instanceof Error ? e.message : String(e));
        onFinishError?.(new Error(msg));
      }
    })();
  };

  window.addEventListener('message', onMessage);
  cleanupRef.current = () => {
    window.clearTimeout(ttl);
    window.removeEventListener('message', onMessage);
  };
}

export default ConnectWhatsAppEmbeddedButton;
